import {
  PathPickMarket,
  PathPickMarketPriceBasis,
  PathPickMarketPriceValues,
  PathPickProduct,
} from "@/features/pathpick/types";

// 마켓 이름은 브랜드 표기라 번역하지 않는다 — i18n 문구의 {{market}} 자리에 그대로 넣는다.
export const PATH_PICK_MARKET_LABEL: Record<PathPickMarket, string> = {
  KREAM: "KREAM",
  POIZON: "POIZON",
};

// 선언 순서가 카드·상세·옵션 표의 표시 순서다(항상 KREAM 다음 POIZON).
export const PATH_PICK_MARKETS: PathPickMarket[] = ["KREAM", "POIZON"];

// 기준가 라벨은 마켓마다 다르다 — 같은 lastValue 라도 크림은 "최근 체결가",
// 포이즌은 "30일 평균 거래가"다(성질이 달라 같은 말로 묶으면 오해를 만든다).
export const PATH_PICK_MARKET_BASIS_LABEL_KEY: Record<
  PathPickMarket,
  Record<PathPickMarketPriceBasis, string>
> = {
  KREAM: {
    bidPrice: "path_pick:market.kream_bid_price",
    tradePrice: "path_pick:market.kream_trade_price",
  },
  POIZON: {
    bidPrice: "path_pick:market.poizon_bid_price",
    tradePrice: "path_pick:market.poizon_trade_price",
  },
};

// 판매량 라벨 — 크림은 누적 체결수, 포이즌은 30일 롤링이라 라벨도 분리한다.
export const PATH_PICK_MARKET_SALES_LABEL_KEY: Record<PathPickMarket, string> =
  {
    KREAM: "path_pick:market.kream_sales_count",
    POIZON: "path_pick:market.poizon_sales_count",
  };

// 비교 기준에 맞는 가격을 꺼낸다. 해당 시세가 없으면 null.
export const getMarketPriceByBasis = (
  price: PathPickMarketPriceValues | null | undefined,
  basis: PathPickMarketPriceBasis
): number | null =>
  (basis === "tradePrice" ? price?.lastValue : price?.lowestValue) ?? null;

// 마켓 매칭 여부 — 두 시세 중 하나라도 있으면 매칭이다.
// 고른 기준의 값만 비어 있는 건 미매칭이 아니라 "그 시세가 아직 없음"이라,
// 미매칭 안내 대신 "-"로 비워야 기준을 토글할 때 매칭 여부가 뒤집혀 보이지 않는다.
export const hasAnyMarketPrice = (
  price?: PathPickMarketPriceValues | null
): boolean => price?.lowestValue != null || price?.lastValue != null;

export const getPathPickMarketPrice = (
  item: Pick<PathPickProduct, "kream" | "poizon">,
  market: PathPickMarket
): (PathPickMarketPriceValues & { currency?: string }) | null =>
  (market === "KREAM" ? item.kream?.price : item.poizon?.price) ?? null;

export const isPathPickMarketMatched = (
  item: Pick<PathPickProduct, "kream" | "poizon">,
  market: PathPickMarket
): boolean => hasAnyMarketPrice(getPathPickMarketPrice(item, market));

// 마진을 낼 수 있는 마켓인지 — 크림만 체결수가 적으면(서버 marginAvailable=false) 막힌다.
// 매칭 여부와는 별개다: 매칭(시세 표시)은 그대로 두고 마진 자리만 "계산 불가"가 된다.
// 포이즌은 이 구분이 없어 항상 true.
export const isPathPickMarketMarginAvailable = (
  item: Pick<PathPickProduct, "kream" | "poizon">,
  market: PathPickMarket
): boolean =>
  market === "KREAM" ? item.kream?.marginAvailable !== false : true;

export interface PathPickMargin {
  expectedMargin: number | null;
  // 마진율(%) — 공급가가 아니라 **기준가**로 나눈 판매가 대비 마진율이다.
  marginRate: number | null;
}

// 예상 마진 = 선택 기준가 − 예상 공급가, 마진율 = 예상 마진 ÷ 기준가 × 100.
// 상품 레벨과 옵션 레벨이 같은 계산을 쓰도록 한 함수로 모아 둔다 —
// 흩어지면 카드와 옵션 표의 마진율이 조용히 달라진다.
export const getPathPickMargin = (
  basisValue: number | null,
  supplyPrice: number
): PathPickMargin => {
  if (basisValue == null) {
    return { expectedMargin: null, marginRate: null };
  }
  const expectedMargin = basisValue - supplyPrice;
  return {
    expectedMargin,
    // 기준가가 0이면 비율을 낼 수 없다(마진 금액은 그대로 보여준다).
    marginRate: basisValue > 0 ? (expectedMargin / basisValue) * 100 : null,
  };
};

export interface PathPickMarketMetrics extends PathPickMargin {
  market: PathPickMarket;
  // 시세가 하나라도 있으면 매칭 — false면 카드/상세가 "{마켓} 미매칭"으로 그린다.
  isMatched: boolean;
  // 고른 비교 기준의 값. 미매칭이거나 그 기준의 시세만 없으면 null.
  // 마진을 못 내는 경우(isMarginAvailable=false)에도 기준가는 그대로 채운다 — 막히는 건 마진뿐.
  basisValue: number | null;
  // false면 매칭이어도 마진 칸이 "계산 불가"가 된다(크림 체결수 부족).
  isMarginAvailable: boolean;
  lowestValue: number | null;
  lastValue: number | null;
  // KREAM=누적 체결수, POIZON=30일 판매량. 서로 비교하면 안 된다.
  salesCount: number | null;
}

// 마켓 1개의 화면용 지표 한 묶음 — 카드의 마켓 블록, 상세 모달의 마켓 카드가 이걸 쓴다.
export const getPathPickMarketMetrics = (
  item: PathPickProduct,
  market: PathPickMarket,
  basis: PathPickMarketPriceBasis
): PathPickMarketMetrics => {
  const price = getPathPickMarketPrice(item, market);
  const basisValue = getMarketPriceByBasis(price, basis);
  const isMarginAvailable = isPathPickMarketMarginAvailable(item, market);

  return {
    market,
    isMatched: hasAnyMarketPrice(price),
    basisValue,
    isMarginAvailable,
    lowestValue: price?.lowestValue ?? null,
    lastValue: price?.lastValue ?? null,
    salesCount:
      (market === "KREAM" ? item.kream?.salesCount : item.poizon?.sales_30d) ??
      null,
    // 마진을 못 내는 마켓은 기준가가 있어도 마진을 비운다(호출부가 "계산 불가"를 그린다).
    ...getPathPickMargin(
      isMarginAvailable ? basisValue : null,
      item.salePrice.value
    ),
  };
};

// 포이즌은 크림 같은 공개 상품 페이지가 없어 셀러센터의 입찰 등록 화면으로 보낸다 —
// globalSpuIds 에 spuId(globalSpuId)를 넣으면 그 상품이 선택된 채로 열린다.
const POIZON_SELLER_BID_URL =
  "https://seller.poizon.com/main/bid/freely/operateBidList";

// 마켓 외부 링크 — 미매칭 마켓은 null이라 호출부가 ↗ 아이콘 자체를 그리지 않는다.
// 포이즌 주소는 로그인이 필요한 판매자 센터라, 호출부가 바로 열지 않고
// PathPickPoizonSellerCenterModal 로 한 번 알린 뒤 연다.
// 상품 전체가 아니라 링크에 쓰는 세 필드만 받는다 — 패스픽 상품이 아닌 곳(오더시트 시세 열)에서도
// 같은 주소 규칙을 쓰기 위해서다.
export const getPathPickMarketExternalUrl = (
  item: Pick<PathPickProduct, "kream" | "poizon" | "sku">,
  market: PathPickMarket
): string | null => {
  if (!isPathPickMarketMatched(item, market)) {
    return null;
  }

  if (market === "POIZON") {
    const spuId = item.poizon?.spuId;
    // spuId 가 없으면 열어도 상품이 안 잡힌다 — 크림처럼 검색으로 대체할 주소가
    // 없어 링크를 만들지 않는다(호출부가 ↗ 를 감춘다).
    return spuId
      ? `${POIZON_SELLER_BID_URL}?operateType=add&sourceName=1&globalSpuIds=${spuId}`
      : null;
  }

  const kreamProductId = item.kream?.productId;
  return kreamProductId
    ? `https://kream.co.kr/products/${kreamProductId}`
    : `https://kream.co.kr/search?keyword=${item.sku}`;
};

export interface PathPickMarketRecommendation {
  market: PathPickMarket;
  margin: number;
  comparedMarket: PathPickMarket;
  // 두 마진의 차액(0 이상). 0이면 호출부가 "더 높음" 줄을 생략한다.
  diff: number;
}

// 추천 마켓 — 마진이 높은 쪽. **양쪽 마진이 다 있을 때만** 결과가 나온다.
// 한쪽만 있는 상품을 그 마켓으로 추천하면 "비교해서 고른 것"처럼 읽히는데
// 실제로는 비교 대상이 없다 — 그래서 null을 주고 호출부가 추천 줄을 감춘다.
export const getPathPickMarketRecommendation = (
  kream: Pick<PathPickMarketMetrics, "market" | "expectedMargin">,
  poizon: Pick<PathPickMarketMetrics, "market" | "expectedMargin">
): PathPickMarketRecommendation | null => {
  if (kream.expectedMargin == null || poizon.expectedMargin == null) {
    return null;
  }
  // 동률이면 KREAM을 앞세운다(표시 순서와 같게 두어 값이 같을 때 순서가 흔들리지 않게).
  const [winner, loser] =
    kream.expectedMargin >= poizon.expectedMargin
      ? [kream, poizon]
      : [poizon, kream];

  return {
    market: winner.market,
    margin: winner.expectedMargin as number,
    comparedMarket: loser.market,
    diff: (winner.expectedMargin as number) - (loser.expectedMargin as number),
  };
};
